import { useEffect, useState, useRef } from "react";
import { WIDGET_ICONS } from "../../../../../../constants";
import ImageRadio from "../../../../../../parts/inputs/image-radio";
import scriptLoader from "../../../../../../utils/scriptLoader";
import { isPullActiveCoach } from "../../../../../../utils/user-helpers";
import {
  deleteWidgetUtil,
  orderWidgets,
} from "../../../../../../utils/widgets";

import submit from "submit-form";
import { getUserToRequest } from "../../../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import ConfirmationModal from "../../../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../../../parts/SvgIcon";
import WidgetManagerHeader from "./WidgetManagerHeader";
import { EditWidgetInfoModal } from "../../../../../../parts/modals/EditWidgetInfo";
import useCompareState from "../../../../../../hooks/useCompareState";
import { EditDailyAccountability } from "./EditDailyAccountability";
import SelectDays from "../../../../../../parts/inputs/select-days";

const switchTemplateStatus = (id, status) => {
  submit(`/actions/widget-daily-accountability/status/${id}`, {
    method: "POST",
    body: { status },
  });
};

const handleDelete = (e, id) => {
  if (
    process.browser &&
    confirm("Are you sure you want to delete this template?")
  ) {
    e.stopPropagation();
    e.preventDefault();
    submit(`/actions/widget-daily-accountability/delete/${id}`, {
      method: "GET",
    });
  }
};

const TemplateToggle = ({ status, id }) => {
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);
  const isPublished = status === "published";

  const handleChange = () => {
    switchTemplateStatus(id, isPublished ? "draft" : "published");
    setTimeout(() => {
      setOpenedConfirmationModal(false);
    }, 500);
  };
  return (
    <>
      <input
        type="checkbox"
        id={`switch-${id}`}
        name={`switch-${id}`}
        role="switch"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onChange={(e) => {
          setOpenedConfirmationModal(!openedConfirmationModal);
        }}
        checked={isPublished}
        className="ml-4"
      />
      <ConfirmationModal
        title={isPublished ? "Unpublish" : "Publish"}
        contentText={`Are you sure you want to ${
          isPublished ? "unpublish" : "publish"
        } this template?`}
        cancelButtonText="Cancel"
        cancelButtonAction={(e) => {
          e.stopPropagation();
          setOpenedConfirmationModal(false);
        }}
        confirmButtonText={isPublished ? "Unpublish" : "Publish"}
        confirmButtonAction={(e) => {
          e.stopPropagation();
          handleChange();
        }}
        isOpen={openedConfirmationModal}
        icon="delete"
        confirmSuccess={!isPublished}
      />
    </>
  );
};

export const DailyAccountabilityWidgetManager = ({ locals, goBack }) => {
  const {
    activeUser,
    activeWidget,
    dailyAccountabilityTemplates,
    organizationOwner,
  } = locals;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);

  const [formState, setFormState] = useState({});
  const [dailyAccountabilityState, setDailyAccountabilityState] = useState([]);
  const [addNew, setAddNew] = useState("addNewButton");
  const [statusState, setStatusState] = useState("save");
  const [refreshList, setRefreshList] = useState(false);
  const [editWidgetInfoModalOpened, setEditWidgetInfoModalOpened] =
    useState(false);
  //state to control the accordion state
  const [accordionState, setAccordionState] = useState({
    isOpen: false,
    _id: "",
  });
  const [changedOrder, setChangedOrder] = useState(false);

  useEffect(() => {
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@latest/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                const ordered = Array.from(items).map((element) => {
                  const listInput = element.querySelector(".list-section-div");
                  const idInput = element.querySelector(".id-section-div");
                  const nameInput = element.querySelector(".name-section-div");
                  const statusInput = element.querySelector(
                    ".status-section-div"
                  );

                  return {
                    widget: activeWidget._id,
                    _id: (idInput && idInput.value) || "",
                    status: (statusInput && statusInput.value) || "",
                    name: (nameInput && nameInput.textContent) || "",
                    list:
                      (listInput?.textContent &&
                        JSON.parse(listInput.textContent)) ||
                      "",
                  };
                });
                setDailyAccountabilityState(ordered);
                setChangedOrder(true);
                return false;
              },
            }
          );
        }
      );
    }
  }, []);

  const [compareBase, setCompareBase, setCurrentCompareState, hasChanged] =
    useCompareState();
  useEffect(() => {
    if (activeWidget) {
      setFormState({
        id: activeWidget._id,
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        order: activeWidget.order,
        status: activeWidget.status,
        type: activeWidget.type,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,

        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      });
      const comparingBase = {
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,
        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      };
      setCompareBase(comparingBase);
      setCurrentCompareState(comparingBase);
    }
    if (dailyAccountabilityTemplates) {
      setDailyAccountabilityState(dailyAccountabilityTemplates);
    }
  }, [activeWidget, dailyAccountabilityTemplates]);

  useEffect(() => {
    if (locals.query.submited) {
      setAddNew("addNewButton");
    }
  }, [locals.query]);

  const prevTemplates = useRef(dailyAccountabilityTemplates);
  useEffect(() => {
    const previousTemplates = prevTemplates.current;
    const currentTemplates = dailyAccountabilityTemplates;

    if (currentTemplates.length > previousTemplates.length) {
      const newTemplate = currentTemplates.at(-1);
      // Update accordionState to open with the new template
      setAccordionState({
        isOpen: true,
        _id: newTemplate._id,
        name: newTemplate.name,
        list: newTemplate.list,
      });
    }
    // Update the ref with the current templates after comparison
    prevTemplates.current = currentTemplates;
  }, [dailyAccountabilityTemplates]);

  const {
    description = "",
    name = "",
    icon = "",
    id = 0,
    pushPermission = true,
    pullPermission = true,
  } = formState;

  const handleSaveReorder = (e) => {
    e.preventDefault();
    const orderedTemplates = dailyAccountabilityState.map(
      (element) => element._id
    );
    if (orderedTemplates.length > 0) {
      setStatusState("save");
      submit("/actions/widget-daily-accountability/reorder", {
        method: "POST",
        body: { orderedTemplates },
      });
    }
  };
  const cancelReorder = () => {
    setDailyAccountabilityState(dailyAccountabilityTemplates);
    setStatusState("save");
    setRefreshList(!refreshList);
  };

  const validationSubmit = () => {
    return !!(description.length > 0 && name.length > 0 && icon.length > 0);
  };

  const handleInputChange = ({ target }) => {
    const targetName = target?.name.replace("-mobile", "") || target?.name;
    const newState = { ...formState, [targetName]: target.value };

    setFormState(newState);
    setCurrentCompareState({
      ...compareBase,
      [targetName]: target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = formState;
    delete body.order;
    delete body.status;
    delete body.type;
    delete body.id;

    submit(`/actions/widgets/save/${id}`, {
      method: "POST",
      body: body,
    });
  };
  const handleGoToMain = () => {
    if (accordionState.isOpen) {
      setAccordionState({ isOpen: false, _id: "" });
    }
    if (addNew === "addNewForm") {
      setAddNew("addNewButton");
    }
  };

  const handleChangeStatusState = (state) => {
    setStatusState(state);
  };

  const deleteWidget = (e) => {
    if (activeWidget) {
      deleteWidgetUtil(e, activeWidget._id);
    }
  };

  const onOpenEditWidgetInfo = () => {
    setEditWidgetInfoModalOpened(!editWidgetInfoModalOpened);
  };

  const WidgetTogglePush = ({ permission }) => {
    return (
      <div className="labeled-switch" sz="large">
        <span>Subscriptions (PUSH)</span>
        <input
          type="checkbox"
          id="switch-1"
          name="switch-1"
          role="switch"
          onChange={() => {
            const newState = {
              ...formState,
              pushPermission: !formState.pushPermission,
            };
            setFormState(newState);
            setCurrentCompareState({
              ...compareBase,
              pushPermission: !formState.pushPermission,
            });
          }}
          checked={permission ? true : undefined}
        />
      </div>
    );
  };

  const WidgetTogglePull = ({ permission }) => {
    return (
      <div className="labeled-switch" sz="large">
        <span>Coaching (PULL)</span>
        <input
          type="checkbox"
          id="switch-1"
          name="switch-1"
          role="switch"
          onChange={() => {
            const newState = {
              ...formState,
              pullPermission: !formState.pullPermission,
            };
            setFormState(newState);
            setCurrentCompareState({
              ...compareBase,
              pullPermission: !formState.pullPermission,
            });
          }}
          checked={permission ? true : undefined}
        />
      </div>
    );
  };

  return (
    <main className="widgets-manager list daily-accountability">
      <WidgetManagerHeader
        name={name}
        description={description}
        goBack={goBack}
        onOpenEditWidgetInfo={onOpenEditWidgetInfo}
      />
      <EditWidgetInfoModal
        widgetName={formState.name}
        widgetDescription={formState.description}
        widgetIcon={formState.icon}
        isOpen={editWidgetInfoModalOpened}
        onClose={onOpenEditWidgetInfo}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        validationSubmit={validationSubmit}
        iconsRadios={WIDGET_ICONS["daily-accountability-template"]}
        WidgetTogglePull={WidgetTogglePull}
        WidgetTogglePush={WidgetTogglePush}
        userToRequest={userToRequest}
        pullPermission={pullPermission}
        pushPermission={pushPermission}
      />
      <div>
        {!!activeWidget && (
          <aside className="mobile-hidden">
            <div className="overflow-auto max-h-[calc(100vh-16.75rem)]">
              <label htmlFor="name"> Widget name </label>
              <input
                name="name"
                className="text-input"
                placeholder={`Enter Name`}
                value={name}
                maxLength="30"
                onChange={handleInputChange}
              />
              <label htmlFor="description"> Widget description </label>
              <textarea
                maxLength="60"
                name="description"
                className="text-input"
                placeholder={`Enter description here..`}
                value={description}
                onChange={handleInputChange}
              />

              <ImageRadio
                label="Widget icon"
                name="icon"
                currentValue={icon}
                radios={WIDGET_ICONS["daily-accountability-template"]}
                onChange={handleInputChange}
                isManager={true}
              />

              <label>User type access</label>
              {isPullActiveCoach(userToRequest) && (
                <WidgetTogglePull permission={pullPermission} />
              )}
              {userToRequest.pushAccountInfo && (
                <WidgetTogglePush permission={pushPermission} />
              )}
            </div>
            <footer>
              <button
                data-button-type="secondary"
                data-action="cancel"
                onClick={deleteWidget}
              >
                Delete
              </button>
              <button
                data-button-type="primary"
                onClick={handleSubmit}
                disabled={!hasChanged}
              >
                Save
              </button>
            </footer>
          </aside>
        )}
        {!!dailyAccountabilityTemplates && (
          <section>
            <header>
              <section className="only-show-mobile back-handler">
                <SvgIcon
                  src={"/img/new-icons/back-button.svg"}
                  onClickFn={() =>
                    submit(`/actions/refresh/?`, {
                      method: "GET",
                      body: { section: "", learn: "" },
                    })
                  }
                />
                <h3>{activeWidget?.name || "Explore"}</h3>
              </section>
              <hgroup className="mobile-hidden">
                {(addNew === "addNewForm" || accordionState.isOpen) && (
                  <SvgIcon
                    src="/img/new-icons/back-button.svg"
                    onClickFn={handleGoToMain}
                    classNames="cursor-pointer"
                  />
                )}
                <div>
                  <h4>
                    {addNew === "addNewForm"
                      ? "Add New Daily Accountability Template"
                      : accordionState.isOpen
                      ? accordionState.name
                      : "Daily Accountability Templates"}
                  </h4>
                </div>
              </hgroup>

              {statusState === "save" &&
                !accordionState.isOpen &&
                addNew === "addNewButton" &&
                dailyAccountabilityState.length > 1 && (
                  <button
                    data-button-type="secondary"
                    onClick={() => handleChangeStatusState("change-order")}
                  >
                    Change order
                  </button>
                )}
              {statusState === "change-order" && (
                <hgroup className="actions-wrapper">
                  <button
                    data-button-type="primary"
                    onClick={handleSaveReorder}
                    disabled={!changedOrder}
                  >
                    Save
                  </button>
                  <button
                    data-button-type="secondary"
                    data-action="cancel"
                    onClick={cancelReorder}
                  >
                    Cancel
                  </button>
                </hgroup>
              )}
            </header>
            {addNew === "addNewButton" && !accordionState.isOpen && (
              <div className="principal-grabber-div">
                {orderWidgets(dailyAccountabilityState).map(
                  ({ _id, name, status, list }) => {
                    return (
                      <div
                        className="article-wrapper daily-accountability"
                        key={`${_id}-${refreshList}`}
                      >
                        <article
                          id={_id}
                          className={
                            statusState === "change-order"
                              ? "columns column item-grabber-container"
                              : "columns item-grabber-container"
                          }
                        >
                          <div style={{ display: "none" }}>
                            <input
                              className="status-section-div"
                              name="status"
                              readOnly
                              value={status}
                            />
                            <input
                              className="id-section-div"
                              name="id"
                              readOnly
                              value={_id}
                            />
                            <span className="list-section-div">
                              {JSON.stringify(list)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center gap-4 flex-grow px-4 pb-4">
                            <p className="s2 name-section-div">{name}</p>
                            <div className="flex items-center gap-2">
                              <button
                                data-button-type="secondary"
                                data-icon-only
                              >
                                <SvgIcon
                                  src="/img/new-icons/edit-pencil.svg"
                                  onClickFn={() => {
                                    setAccordionState({
                                      _id,
                                      name,
                                      list,
                                      isOpen: true,
                                    });
                                  }}
                                />
                              </button>
                              <button
                                data-button-type="secondary"
                                data-icon-only
                              >
                                <SvgIcon
                                  src={"/img/new-icons/red-trash.svg"}
                                  onClickFn={(event) =>
                                    handleDelete(event, _id)
                                  }
                                  noFilterOnLight={true}
                                />
                              </button>
                              <TemplateToggle status={status} id={_id} />
                            </div>
                          </div>

                          {list?.length > 0 &&
                            list.map((item) => (
                              <div
                                key={item._id}
                                className="accountability-list"
                              >
                                <p className="p5">{item.title}</p>
                                <SelectDays days={item.days} />
                              </div>
                            ))}
                        </article>
                        {statusState === "change-order" && (
                          <div className="drag-wrapper">
                            <SvgIcon
                              src="/img/new-icons/menu.svg"
                              className="drag-handle mobile-hidden"
                              mobileSrc={"/img/new-icons/drag.svg"}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            )}
            {accordionState.isOpen && (
              <EditDailyAccountability
                _id={accordionState._id}
                nameProp={accordionState.name}
                list={accordionState.list}
                widget={activeWidget._id}
              />
            )}
            {addNew === "addNewButton" && !accordionState.isOpen && (
              <div
                className="dashed-box"
                onClick={() => {
                  setAccordionState({ isOpen: false, _id: "" });
                  setAddNew("addNewForm");
                }}
              >
                <SvgIcon
                  src="/img/new-icons/add-square-outline.svg"
                  alt="back"
                />
                <p className="p4">Add New Daily Accountability Template</p>
              </div>
            )}
            {addNew === "addNewForm" && !accordionState.isOpen && (
              <EditDailyAccountability widget={activeWidget._id} />
            )}
            {addNew === "addNewButton" ||
              (addNew === "editForm" && (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className={
                      validationSubmit()
                        ? "save-macro-btn"
                        : "save-macro-disabled-btn save-macro-btn"
                    }
                    onClick={handleSubmit}
                    disabled={!validationSubmit()}
                  >
                    Save
                  </button>
                </div>
              ))}
          </section>
        )}
      </div>
    </main>
  );
};
